import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    uploadIracingJson,
    runStandingsImportSingleClass,
    streamJobLogs,
    getJob,
} from "../../../lib/ligamanagerApi";
import { LEAGUE_OPTIONS_SINGLE_CLASS } from "../../../lib/leagueSheetsID";

function prettyErr(e) {
    if (!e) return "Unbekannter Fehler";
    if (typeof e === "string") return e;
    if (e instanceof Error) return e.message || "Fehler";
    if (typeof e === "object") {
        if (e.detail) return typeof e.detail === "string" ? e.detail : JSON.stringify(e.detail, null, 2);
        if (e.message) return e.message;
        try {
            return JSON.stringify(e, null, 2);
        } catch {
            return String(e);
        }
    }
    return String(e);
}

function isDividerMsg(msg) {
    const s = String(msg || "").trim();
    if (!s) return false;
    return /^-+$/.test(s) && s.length >= 20;
}

function classifyLine(msg) {
    const s = String(msg || "");
    if (s.startsWith("[") && s.includes("]")) return "step";
    if (s.startsWith("    ")) return "indent";
    return "normal";
}

function downloadText(filename, text) {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function downloadJson(filename, obj) {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function ProgressBar({ percent, labelLeft, labelRight }) {
    const p = Math.max(0, Math.min(100, Number(percent || 0)));
    return (
        <div style={{ width: "100%", marginTop: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", opacity: 0.85, fontFamily: "var(--ff-Everett)", fontSize: 12 }}>
                <div>{labelLeft}</div>
                <div>{labelRight}</div>
            </div>
            <div
                style={{
                    marginTop: 6,
                    height: 10,
                    borderRadius: 999,
                    border: "1px solid rgba(232,253,83,.35)",
                    background: "rgba(232,253,83,.08)",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        height: "100%",
                        width: `${p}%`,
                        background: "rgba(232,253,83,.75)",
                    }}
                />
            </div>
        </div>
    );
}

export default function StandingsImportPanel() {
    const [sheetIdOrUrl, setSheetIdOrUrl] = useState(LEAGUE_OPTIONS_SINGLE_CLASS[0]?.value || "");
    const [tag, setTag] = useState("R1");
    const [file, setFile] = useState(null);

    const [uploadId, setUploadId] = useState("");
    const [uploadName, setUploadName] = useState("");

    const [jobId, setJobId] = useState(null);
    const stopStreamRef = useRef(null);

    // Logs: intern rich
    const [logItems, setLogItems] = useState([]); // {seq, level, ts, msg, uiOnly?}
    const [error, setError] = useState("");

    const [uploadBusy, setUploadBusy] = useState(false);
    const [importBusy, setImportBusy] = useState(false);

    // Default: Details AUS
    const [showDetails, setShowDetails] = useState(false);

    // Filter + autoscroll
    const [autoScroll, setAutoScroll] = useState(true);
    const [filterInfo, setFilterInfo] = useState(true);
    const [filterWarn, setFilterWarn] = useState(true);
    const [filterError, setFilterError] = useState(true);
    const [filterUi, setFilterUi] = useState(true);

    // Progress (poll)
    const [progress, setProgress] = useState({
        status: "",
        step: 0,
        total: 0,
        label: "",
    });
    const jobPollTimerRef = useRef(null);

    const scrollBoxRef = useRef(null);
    const stickToBottomRef = useRef(true);

    const busy = uploadBusy || importBusy;
    const fileLabel = file ? file.name : "JSON auswählen…";

    function pushUiInfo(msg) {
        setLogItems((prev) => [
            ...prev,
            { seq: null, level: "INFO", ts: null, msg: String(msg || ""), uiOnly: true },
        ]);
    }

    function pushLogItem(item) {
        let level = (item?.level || "INFO").toUpperCase();
        if (!["INFO", "WARN", "ERROR"].includes(level)) level = "INFO";
        const msg = item?.msg ?? "";
        const ts = item?.ts ?? null;
        const seq = typeof item?.seq === "number" ? item.seq : null;

        setLogItems((prev) => [...prev, { seq, level, ts, msg, uiOnly: false }]);
    }

    function stopStream() {
        try {
            if (stopStreamRef.current) stopStreamRef.current();
        } catch {}
        stopStreamRef.current = null;
    }

    function stopJobPolling() {
        if (jobPollTimerRef.current) {
            clearInterval(jobPollTimerRef.current);
            jobPollTimerRef.current = null;
        }
    }

    // stop stream & poll on unmount
    useEffect(() => {
        return () => {
            stopStream();
            stopJobPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-scroll
    useEffect(() => {
        const el = scrollBoxRef.current;
        if (!el) return;
        if (!autoScroll) return;
        if (!stickToBottomRef.current) return;
        el.scrollTop = el.scrollHeight;
    }, [logItems, autoScroll]);

    function onScrollLogs() {
        const el = scrollBoxRef.current;
        if (!el) return;
        const nearBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 40;
        stickToBottomRef.current = nearBottom;
    }

    async function onUpload() {
        setError("");
        setLogItems([]);
        setUploadId("");
        setUploadName("");
        setJobId(null);
        setProgress({ status: "", step: 0, total: 0, label: "" });

        stopStream();
        stopJobPolling();

        if (!file) return setError("Bitte zuerst eine JSON Datei auswählen.");

        setUploadBusy(true);
        try {
            pushUiInfo("Upload startet …");
            const r = await uploadIracingJson(file);
            if (!r?.upload_id) throw new Error("Upload fehlgeschlagen: Keine upload_id erhalten.");

            setUploadId(r.upload_id);
            setUploadName(r.filename || file.name);
            pushUiInfo(`Upload OK: ${r.upload_id} (${r.filename || file.name})`);
        } catch (e) {
            const msg = prettyErr(e);
            setError(msg);
            pushUiInfo(`FEHLER: ${msg}`);
        } finally {
            setUploadBusy(false);
        }
    }

    function startJobPolling(id) {
        stopJobPolling();

        const pollOnce = async () => {
            try {
                const j = await getJob(id);
                setProgress({
                    status: j?.status || "",
                    step: Number(j?.progress_step || 0),
                    total: Number(j?.progress_total || 0),
                    label: j?.progress_label || "",
                });

                // stop poll if done/error/canceled
                if (j?.status && ["done", "error", "canceled"].includes(String(j.status))) {
                    stopJobPolling();
                }
            } catch {
                // polling errors shouldn't kill UI; keep going
            }
        };

        // direct first tick
        pollOnce();

        jobPollTimerRef.current = setInterval(pollOnce, 700);
    }

    async function onRunImport() {
        setError("");
        setLogItems([]);
        setJobId(null);
        setProgress({ status: "", step: 0, total: 0, label: "" });

        stopStream();
        stopJobPolling();

        if (!sheetIdOrUrl.trim()) return setError("Bitte Sheet ID/URL eintragen.");
        if (!tag.trim()) return setError("Bitte Renntag (z.B. R6) eintragen.");
        if (!uploadId) return setError("Bitte erst JSON hochladen (Upload ID fehlt).");

        setImportBusy(true);
        try {
            pushUiInfo("Job wird gestartet …");

            const r = await runStandingsImportSingleClass({
                sheetInput: sheetIdOrUrl.trim(),
                tag: tag.trim().toUpperCase(),
                upload_id: uploadId,
            });

            if (!r?.job_id) throw new Error("Backend hat kein job_id zurückgegeben (standings-import-single-class).");

            setJobId(r.job_id);
            pushUiInfo(`Job gestartet: #${r.job_id}`);
            pushUiInfo("Live Logs verbinden …");

            // start progress polling
            startJobPolling(r.job_id);

            const stopFn = streamJobLogs({
                job_id: r.job_id,
                onLog: (item) => pushLogItem(item),
                onDone: (status) => {
                    pushUiInfo(`Job beendet: ${status}`);
                    if (String(status).trim() === "error") setError("Job beendet mit Fehler. Siehe Logs.");
                    setImportBusy(false);
                    stopStream();
                    // Polling stops by itself when status becomes done/error, but stop here too:
                    stopJobPolling();
                },
                onError: (e) => {
                    const msg = prettyErr(e);
                    pushUiInfo(`SSE FEHLER: ${msg}`);
                    setError(msg);
                    setImportBusy(false);
                    stopStream();
                    stopJobPolling();
                },
            });

            stopStreamRef.current = stopFn;
        } catch (e) {
            const msg = prettyErr(e);
            setError(msg);
            pushUiInfo(`FEHLER: ${msg}`);
            setImportBusy(false);
            stopStream();
            stopJobPolling();
        }
    }

    function onClearLogs() {
        setLogItems([]);
        setError("");
    }

    function onDownloadJson() {
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const fname = `racevision_job_${jobId || "nojob"}_logs_${ts}.json`;
        downloadJson(fname, {
            job_id: jobId,
            upload_id: uploadId,
            tag,
            sheet_input: sheetIdOrUrl,
            logs: logItems,
            progress,
        });
    }

    function onDownloadTxt() {
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const fname = `racevision_job_${jobId || "nojob"}_logs_${ts}.txt`;

        const lines = logItems.map((it) => {
            const msg = String(it.msg ?? "");
            if (!showDetails) return msg;
            return JSON.stringify({ seq: it.seq, level: it.level, ts: it.ts, msg: it.msg }, null, 0);
        });

        downloadText(fname, lines.join("\n"));
    }

    const renderedItems = useMemo(() => {
        const allowLevel = (lvl, uiOnly) => {
            const L = (lvl || "INFO").toUpperCase();
            if (uiOnly) return !!filterUi;
            if (L === "INFO") return !!filterInfo;
            if (L === "WARN") return !!filterWarn;
            if (L === "ERROR") return !!filterError;
            return true;
        };

        return logItems
            .filter((it) => allowLevel(it.level, !!it.uiOnly))
            .map((it, idx) => {
                const msg = String(it.msg ?? "");
                const isDiv = isDividerMsg(msg);
                const kind = classifyLine(msg);

                let level = (it.level || "INFO").toUpperCase();
                if (!["INFO", "WARN", "ERROR"].includes(level)) level = "INFO";

                const rawJson = JSON.stringify({ seq: it.seq, level: level, ts: it.ts, msg: it.msg }, null, 0);

                return {
                    key: `${it.seq ?? "x"}-${idx}`,
                    level,
                    ts: it.ts,
                    seq: it.seq,
                    msg,
                    isDivider: isDiv,
                    kind,
                    uiOnly: !!it.uiOnly,
                    rawJson,
                };
            });
    }, [logItems, filterInfo, filterWarn, filterError, filterUi]);

    const filteredCount = renderedItems.length;
    const totalCount = logItems.length;

    const progressPercent =
        progress.total > 0 ? Math.round((Math.max(0, progress.step) / Math.max(1, progress.total)) * 100) : 0;

    return (
        <div className="card card--withbar">
            <h2 style={{ marginTop: 0 }}>Standings Import Single Class</h2>

            <div style={{ display: "grid", gap: 12, width: "100%", maxWidth: 980 }}>
                {/* Sheet */}
                <label style={{ width: "100%" }}>
                    <div style={{ opacity: 0.8, marginBottom: 6, fontFamily: "var(--ff-Everett)" }}>
                        Liga
                    </div>
                    <select
                        className="input"
                        value={sheetIdOrUrl}
                        onChange={(e) => setSheetIdOrUrl(e.target.value)}
                        style={{ width: 260 }}
                        disabled={busy}
                    >
                        {LEAGUE_OPTIONS_SINGLE_CLASS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </label>

                {/* Tag */}
                <label>
                    <div style={{ opacity: 0.8, marginBottom: 6, fontFamily: "var(--ff-Everett)" }}>Renntag</div>
                    <input
                        className="input"
                        value={tag}
                        onChange={(e) => setTag((e.target.value || "").toUpperCase())}
                        placeholder="R6"
                        style={{ width: 160 }}
                        disabled={busy}
                    />
                </label>

                {/* File */}
                <div>
                    <div style={{ opacity: 0.8, marginBottom: 6, fontFamily: "var(--ff-Everett)" }}>
                        iRacing JSON Datei
                    </div>

                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <input
                            id="rv-json-upload"
                            type="file"
                            accept=".json,application/json"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            style={{ display: "none" }}
                            disabled={busy}
                        />

                        <label
                            htmlFor="rv-json-upload"
                            className={`button button--secondary${busy ? " is-disabled" : ""}`}
                            style={{ cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}
                        >
                            {file ? "Datei ändern" : "Datei auswählen"}
                        </label>

                        <div
                            style={{
                                padding: "9px 12px",
                                borderRadius: 12,
                                border: "1px solid var(--border-soft)",
                                background: "rgba(232,253,83,.06)",
                                fontFamily: "var(--ff-Everett)",
                                maxWidth: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                            title={fileLabel}
                        >
                            {fileLabel}
                        </div>
                    </div>
                </div>

                {/* Buttons: 1) Upload in eigener Zeile */}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button className="button button--primary" onClick={onUpload} disabled={busy || !file}>
                        {uploadBusy ? "Upload…" : "1) Upload"}
                    </button>
                </div>

                {/* Buttons: 2) Import starten in eigener Zeile */}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                        className="button button--secondary"
                        onClick={onRunImport}
                        disabled={busy || !uploadId || !sheetIdOrUrl.trim() || !tag.trim()}
                    >
                        {importBusy ? "Import…" : "2) Single Class Import starten"}
                    </button>
                </div>

                {/* Meta */}
                {uploadId && (
                    <div style={{ opacity: 0.9, fontFamily: "var(--ff-Everett)" }}>
                        Upload: <b>{uploadName}</b> – ID: <code>{uploadId}</code>
                    </div>
                )}
                {jobId && (
                    <div style={{ opacity: 0.9, fontFamily: "var(--ff-Everett)" }}>
                        Job: <b>#{jobId}</b>
                    </div>
                )}

                {/* Progressbar */}
                {jobId ? (
                    <ProgressBar
                        percent={progressPercent}
                        labelLeft={
                            progress.label
                                ? `${progress.label} (${progress.status || "..."})`
                                : progress.status
                                    ? `Status: ${progress.status}`
                                    : "Status: …"
                        }
                        labelRight={
                            progress.total > 0 ? `${progress.step}/${progress.total} · ${progressPercent}%` : ""
                        }
                    />
                ) : null}

                {error && <div className="error-box">{error}</div>}

                {/* Accent Trennlinie + Log Bereich Header */}
                <div style={{ marginTop: 10 }}>
                    <div
                        style={{
                            height: 2,
                            width: "100%",
                            borderRadius: 999,
                            background: "rgba(232,253,83,.65)",
                            boxShadow: "0 0 0 1px rgba(232,253,83,.18)",
                            marginBottom: 10,
                        }}
                    />

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <div style={{ opacity: 0.9, marginBottom: 6, fontFamily: "var(--ff-Everett)" }}>
                            Logs
                        </div>
                        <div style={{ opacity: 0.7, fontFamily: "var(--ff-Everett)", fontSize: 12 }}>
                            {totalCount ? `${filteredCount}/${totalCount} Zeilen` : ""}
                        </div>
                    </div>

                    {/* Log-Actions in eigener Zeile */}
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10, alignItems: "center" }}>
                        <button className="button button--secondary" onClick={stopStream} disabled={!stopStreamRef.current}>
                            Logs stoppen
                        </button>
                        <button className="button button--secondary" onClick={onClearLogs} disabled={totalCount === 0}>
                            Clear Logs
                        </button>
                        <button className="button button--secondary" onClick={onDownloadTxt} disabled={totalCount === 0}>
                            Download TXT
                        </button>
                        <button className="button button--secondary" onClick={onDownloadJson} disabled={totalCount === 0}>
                            Download JSON
                        </button>
                    </div>

                    {/* Toggles */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center", opacity: 0.95, marginBottom: 10 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input type="checkbox" checked={showDetails} onChange={(e) => setShowDetails(e.target.checked)} />
                            <span style={{ fontFamily: "var(--ff-Everett)", fontSize: 13 }}>Details anzeigen</span>
                        </label>

                        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} />
                            <span style={{ fontFamily: "var(--ff-Everett)", fontSize: 13 }}>Auto-Scroll</span>
                        </label>

                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                            <span style={{ opacity: 0.75, fontFamily: "var(--ff-Everett)", fontSize: 13 }}>Filter:</span>

                            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <input type="checkbox" checked={filterInfo} onChange={(e) => setFilterInfo(e.target.checked)} />
                                <span style={{ fontFamily: "var(--ff-Everett)", fontSize: 13 }}>INFO</span>
                            </label>

                            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <input type="checkbox" checked={filterWarn} onChange={(e) => setFilterWarn(e.target.checked)} />
                                <span style={{ fontFamily: "var(--ff-Everett)", fontSize: 13 }}>WARN</span>
                            </label>

                            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <input type="checkbox" checked={filterError} onChange={(e) => setFilterError(e.target.checked)} />
                                <span style={{ fontFamily: "var(--ff-Everett)", fontSize: 13 }}>ERROR</span>
                            </label>

                            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <input type="checkbox" checked={filterUi} onChange={(e) => setFilterUi(e.target.checked)} />
                                <span style={{ fontFamily: "var(--ff-Everett)", fontSize: 13 }}>UI</span>
                            </label>
                        </div>
                    </div>

                    {/* Logs Viewer */}
                    <div
                        ref={scrollBoxRef}
                        onScroll={onScrollLogs}
                        className="input"
                        style={{
                            width: "100%",
                            height: 380,
                            overflow: "auto",
                            padding: 12,
                            background: "rgba(5,25,22,0.35)",
                            border: "1px solid rgba(255,255,255,.08)",
                            borderRadius: 12,
                            fontFamily: "var(--ff-Everett)",
                            fontSize: 13,
                            lineHeight: 1.45,
                            color: "var(--text)",
                        }}
                    >
                        {filteredCount === 0 ? (
                            <div style={{ opacity: 0.7 }}>
                                {totalCount === 0 ? "Noch keine Logs…" : "Keine Logs für diesen Filter."}
                            </div>
                        ) : (
                            renderedItems.map((it) => {
                                if (it.isDivider) {
                                    return (
                                        <div key={it.key} style={{ margin: "10px 0", opacity: 0.35 }}>
                                            <div style={{ height: 1, background: "rgba(255,255,255,.18)" }} />
                                        </div>
                                    );
                                }

                                let badgeBg = "rgba(255,255,255,.10)";
                                let badgeBorder = "rgba(255,255,255,.15)";
                                if (it.level === "WARN") {
                                    badgeBg = "rgba(232,253,83,.10)";
                                    badgeBorder = "rgba(232,253,83,.25)";
                                } else if (it.level === "ERROR") {
                                    badgeBg = "rgba(255,80,80,.12)";
                                    badgeBorder = "rgba(255,80,80,.28)";
                                }

                                const isStep = it.kind === "step";
                                const baseOpacity = it.kind === "indent" ? 0.88 : 1;

                                return (
                                    <div
                                        key={it.key}
                                        style={{
                                            display: "flex",
                                            gap: 10,
                                            padding: isStep ? "8px 10px" : "4px 2px",
                                            borderRadius: isStep ? 10 : 0,
                                            background: isStep ? "rgba(255,255,255,.04)" : "transparent",
                                            opacity: baseOpacity,
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        {/* Badge immer */}
                                        <div
                                            style={{
                                                minWidth: 54,
                                                textAlign: "center",
                                                fontSize: 11,
                                                padding: "3px 6px",
                                                borderRadius: 999,
                                                background: badgeBg,
                                                border: `1px solid ${badgeBorder}`,
                                                lineHeight: 1.2,
                                                marginTop: 1,
                                                userSelect: "none",
                                            }}
                                            title={it.level}
                                        >
                                            {it.level}
                                        </div>

                                        <div style={{ whiteSpace: "pre-wrap" }}>
                                            {(() => {
                                                const s = String(it.msg ?? "");
                                                if (s.startsWith("{") && s.includes('"msg"')) {
                                                    try {
                                                        const obj = JSON.parse(s);
                                                        return obj?.msg ?? s;
                                                    } catch {
                                                        // ignore
                                                    }
                                                }
                                                return s;
                                            })()}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}